import { Injectable } from '@angular/core';
import { IUserPrinterViewModel } from '@core/models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PrinterFacadeService } from '@printing/services';
import { State } from '@shared/enums';
import {
	PortalStationNotificationService,
	PrinterNotificationService,
	PrintStationNotificationService,
	UserContextService,
} from '@shared/services';
import _ from 'lodash';
import { BehaviorSubject, combineLatest, map, Observable, switchMap } from 'rxjs';

@UntilDestroy()
@Injectable({
	providedIn: 'root',
})
export class PrinterStateService {
	public printerStateEnum = State;
	public printers$: BehaviorSubject<IUserPrinterViewModel[]> = new BehaviorSubject<IUserPrinterViewModel[]>([]);

	private _onUpdate$: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);
	public selectedPrinter?: IUserPrinterViewModel;

	constructor(
		private _printerFacadeService: PrinterFacadeService,
		userContextService: UserContextService,
		public _printStationNotification: PrintStationNotificationService,
		private _printerNotification: PrinterNotificationService,
		private _portalStationNotificationService: PortalStationNotificationService,
	) {
		combineLatest({
			vendor: userContextService.getVendor(),
			onUpdate: this._onUpdate$,
		})
			.pipe(
				switchMap(response => {
					return this._printerFacadeService.getAllPrinters(response.vendor.id).pipe(
						map(printers => {
							var orderedPrinters = _.orderBy(
								printers,
								['status', 'locationKey', 'locationName', 'displayName'],
								['desc'],
							);
							return orderedPrinters.map(_ => new IUserPrinterViewModel(_));
						}),
					);
				}),
				untilDestroyed(this),
			)
			.subscribe(_ => {
				this.printers$.next([..._]);
			});
	}

	public destroy() {
		this._printStationNotification.removeAllListeners(this);
		this._printerNotification.removeAllListeners(this);
		this._portalStationNotificationService.removeAllListeners(this);
	}

	public init() {
		this._printStationNotification.addStationStateChangedListener(this, this.onStationStateChanged.bind(this));
		this._printStationNotification.addReconnectedListener(this, this.onReconnected.bind(this));

		this._printerNotification.addPrinterStateChangedListener(this, this.onPrinterStateChanged.bind(this));
		this._printerNotification.addPrinterRegisteredListener(this, this.onPrinterRegistered.bind(this));
		this._printerNotification.addPrinterDeletedListener(this, this.onPrinterDeleted.bind(this));
		this._portalStationNotificationService.addStationLinkedListener(this, this.onStationLinked.bind(this));
		this._portalStationNotificationService.addStationRemovedListener(this, this.onStationRemoved.bind(this));
		this._printStationNotification.startConnection();
		this._printerNotification.startConnection();
		this._portalStationNotificationService.startConnection();
	}

	public getPrinters$(): Observable<IUserPrinterViewModel[]> {
		return this.printers$.asObservable();
	}

	private onPrinterStateChanged(clientId: string, state: State, serialNumber?: string, issueDescription?: string) {
		const printers = [...this.printers$.value];
		printers.forEach(printer => {
			if (printer.clientId === clientId && (!serialNumber || printer.serialNumber === serialNumber)) {
				printer.status = state;
				printer.issueDescription = issueDescription;
			}
		});
		this.printers$.next([...printers]);
	}

	private onStationStateChanged(clientId: string, state: State) {
		const printers = [...this.printers$.value];
		printers.forEach(printer => {
			if (printer.clientId === clientId) {
				printer.printStationStatus = state;
			}
		});
		this.printers$.next([...printers]);
	}

	private onStationLinked(clientId: string) {
		this._onUpdate$.next();
	}

	private onPrinterRegistered(clientId: string, serialNumber: string) {
		this._onUpdate$.next();
	}

	private onStationRemoved(clientId: string) {
		const printers = this.printers$.value.filter(s => s.clientId != clientId);
		this.printers$.next([...printers]);
	}

	private onReconnected() {
		this._onUpdate$.next();
	}

	private onPrinterDeleted(clientId: string, serialNumber: string) {
		const printers = this.printers$.value.filter(s => !(s.clientId == clientId && s.serialNumber == serialNumber));
		this.printers$.next([...printers]);
	}
}
